import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageCircle, X, Send } from "lucide-react";
import { API } from "../lib/api";

const CODES = ["+39", "+91", "+1", "+44", "+971", "+33", "+34", "+41", "+49", "+377", "+62", "+66", "+61", "+30", "+351", "+31", "+974", "+966", "+27", "+81", "+65", "+852", "+55", "+52"];
const field = "w-full bg-transparent border border-gold/30 px-3 py-2.5 text-sm outline-none focus:border-gold placeholder:text-ivory/40";

function GuestForm({ onDone }) {
  const [f, setF] = useState({ first_name: "", email: "", code: "+39", phone: "", consent: false });
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const go = (e) => {
    e.preventDefault();
    if (!f.first_name || !f.email || f.phone.replace(/\D/g, "").length < 5) { setErr("Please add your first name, email and WhatsApp or phone."); return; }
    if (!f.consent) { setErr("Please accept the Privacy Notice."); return; }
    onDone({ first_name: f.first_name, email: f.email, phone: `${f.code} ${f.phone}`, consent: true });
  };
  return (
    <form onSubmit={go} className="space-y-3" data-testid="lola-form">
      <input data-testid="lola-firstname" value={f.first_name} onChange={set("first_name")} placeholder="First name *" className={field} />
      <input data-testid="lola-email" type="email" value={f.email} onChange={set("email")} placeholder="Email *" className={field} />
      <div>
        <div className="flex gap-2">
          <select data-testid="lola-code" value={f.code} onChange={set("code")} className={`${field} !w-24 bg-obsidian`}>{CODES.map((c) => <option key={c}>{c}</option>)}</select>
          <input data-testid="lola-phone" type="tel" value={f.phone} onChange={set("phone")} placeholder="WhatsApp or phone *" className={field} />
        </div>
        <p className="text-[0.68rem] text-ivory/55 mt-1">so Team LUXEWELL can reach you personally</p>
      </div>
      <label className="flex items-start gap-2 text-[0.7rem] text-ivory/75 cursor-pointer">
        <input type="checkbox" data-testid="lola-consent" checked={f.consent} onChange={set("consent")} className="accent-[#B0823F] mt-0.5" />
        <span>I agree to be contacted by LUXEWELL and accept the <Link to="/privacy" target="_blank" className="underline decoration-gold">Privacy Notice</Link>.</span>
      </label>
      {err && <p className="text-xs text-red-300" data-testid="lola-error">{err}</p>}
      <button type="submit" data-testid="lola-form-submit" className="btn-gold btn-lux w-full text-center">Let's begin</button>
    </form>
  );
}

export default function LolaConcierge() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [guest, setGuest] = useState(null);
  const [q, setQ] = useState("");
  const [asked, setAsked] = useState("");
  const [answer, setAnswer] = useState("");
  const [state, setState] = useState("idle");
  const ask = async () => {
    if (q.trim().length < 3 || state !== "idle") return;
    setAsked(q.trim()); setState("busy");
    try {
      const res = await fetch(`${API}/lola/concierge`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...guest, question: q.trim() }) });
      if (!res.ok) { const d = await res.json().catch(() => ({})); setAnswer(d.detail || "Lola is unavailable right now. Team LUXEWELL will reply personally within 48 hours to define and design your experience."); setState("done"); return; }
      const reader = res.body.getReader(); const dec = new TextDecoder();
      for (;;) { const { done, value } = await reader.read(); if (done) break; const t = dec.decode(value, { stream: true }); setAnswer((a) => a + t); }
    } catch { setAnswer("Lola is unavailable right now. Team LUXEWELL will reply personally within 48 hours."); }
    setState("done");
  };
  return (
    <>
      <button data-testid="lola-toggle" onClick={() => setOpen(!open)} aria-label="Lola LUXEWELL, your lifestyle concierge"
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center bg-ink text-goldbright border-2 border-goldbright shadow-2xl transition-colors ${open ? "" : "hover:bg-goldbright hover:text-ink"}`}>
        {open ? <X size={22} strokeWidth={2.5} stroke="url(#lola-gold)" data-testid="lola-close-x" /> : <MessageCircle size={20} />}
        <svg width="0" height="0" className="absolute"><defs><linearGradient id="lola-gold" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#B88A3E" /><stop offset="0.45" stopColor="#FFF1BF" /><stop offset="1" stopColor="#BE8F43" /></linearGradient></defs></svg>
      </button>
      {open && (
        <div className="fixed bottom-24 right-4 sm:right-6 z-50 w-[92vw] max-w-sm max-h-[75vh] flex flex-col bg-obsidian border border-[#E8D29B] text-ivory" data-testid="lola-panel">
          <div className="px-5 py-4 border-b border-[#E8D29B]/50 flex items-start justify-between"><div><p className="eyebrow !text-[0.6rem] gold-text">Lola LUXEWELL</p><p className="eyebrow !text-[0.5rem] !text-ivory/70 mt-1">Your Lifestyle Concierge</p></div><img src="/photos/swirl.png" alt="" className="no-grade w-6 h-auto" /></div>
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            <p className="font-headline italic text-lg text-ivory/85 leading-snug">Hello, I'm Lola LUXEWELL. Tell me a little about you, then ask me one question about the experience your life or business needs next.</p>
            {!guest && <GuestForm onDone={setGuest} />}
            {asked && <p className="text-right"><span className="inline-block max-w-[85%] px-4 py-2 text-sm bg-gold/15 text-left" data-testid="lola-msg-user">{asked}</span></p>}
            {(answer || state === "busy") && <p className="px-4 py-2 text-sm leading-relaxed bg-white/5 border-l border-gold/50 whitespace-pre-line" data-testid="lola-msg-lola">{answer || "Lola is thinking…"}</p>}
            {state === "done" && <button onClick={() => { setOpen(false); nav("/"); }} data-testid="lola-explore" className="btn-gold eyebrow !text-[0.62rem] px-5 py-3 outline outline-1 outline-white/90 -outline-offset-[5px]">Explore the House</button>}
          </div>
          {guest && state === "idle" && (
            <div className="p-4 border-t border-gold/30 flex gap-2">
              <input data-testid="lola-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder="Ask Lola one question…" maxLength={1200}
                className="flex-1 bg-transparent border border-gold/30 px-3 py-2 text-sm outline-none focus:border-gold" />
              <button data-testid="lola-send" onClick={ask} aria-label="Send" className="px-3 border border-gold text-gold hover:bg-gold hover:text-ink transition-colors"><Send size={16} /></button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
